import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { apiError, fileUrl, inr, BACKEND_URL } from "../../lib/api";
import { DEFAULT_FIELDS, studentNameFrom, summaryRows } from "../../lib/formFields";
import { pageLangs, localizePage, LANG_LABELS } from "../../lib/pageLang";
import { Logo } from "../../components/Logo";
import {
  Loader2, ArrowRight, ArrowLeft, Copy, Upload, CheckCircle2, ShieldCheck, Sparkles,
  QrCode, MessageCircle, Download, Copy as CopyIcon, AlertCircle, ScanLine, Pencil, Clock, X, Maximize2, Lock, Phone, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Program", "Details", "Summary", "Payment", "Screenshot", "Confirm"];

const cleanHtml = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "h1", "h2", "h3", "ul", "ol", "li", "a", "span", "blockquote"],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:)/i,
});

// Strict Indian mobile check: 10 digits starting 6-9, not all-same-digit,
// not a straight ascending/descending sequence (e.g. 1234567890, 6789012345).
function isValidPhone(val) {
  const s = String(val || "").trim();
  if (!/^[6-9]\d{9}$/.test(s)) return false;
  if (/^(\d)\1{9}$/.test(s)) return false;
  if ("01234567890123456789".includes(s)) return false;
  if ("98765432109876543210".includes(s)) return false;
  return true;
}


export default function PublicPage() {
  const { slug } = useParams();
  const [rawPage, setPage] = useState(null);
  const [lang, setLangState] = useState(() => localStorage.getItem("gdiot_lang") || "en");
  const setLang = (l) => { setLangState(l); localStorage.setItem("gdiot_lang", l); };
  const langs = useMemo(() => pageLangs(rawPage), [rawPage]);
  const page = useMemo(() => localizePage(rawPage, langs.includes(lang) ? lang : "en"), [rawPage, lang, langs]);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(0);

  const [values, setValues] = useState({});
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("QR Code");

  const [preview, setPreview] = useState("");
  const [detected, setDetected] = useState(null);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [paidSoFar, setPaidSoFar] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const fileRef = useRef();
  const [closedMsg, setClosedMsg] = useState("");
  const failed = (e, fallback) => {
    if (e.response?.status === 403) { setClosedMsg(apiError(e.response.data?.detail)); return; }
    toast.error(fallback || apiError(e.response?.data?.detail));
  };

  useEffect(() => {
    api.get(`/public/pages/${slug}`).then((r) => {
      setPage(r.data);
      const f = (r.data.form_fields && r.data.form_fields.length ? r.data.form_fields : DEFAULT_FIELDS).filter((fd) => fd.required);
      const init = {};
      f.forEach((fd) => {
        if (fd.type === "checkbox") init[fd.key] = false;
        else if (fd.key === "school_name" && r.data.school_name) init[fd.key] = r.data.school_name;
        else init[fd.key] = "";
      });
      setValues(init);
    }).catch((e) => setErr(apiError(e.response?.data?.detail) || "Page not found"));
  }, [slug]);

  const setV = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  if (err) return <CenterMsg title="Page not available" msg={err} />;
  if (closedMsg) return <CenterMsg title="This Batch is Currently Full" msg={closedMsg} />;
  if (!page) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const fee = page.final_fee;
  const fields = (page.form_fields && page.form_fields.length ? page.form_fields : DEFAULT_FIELDS).filter((f) => f.required);

  const submitRegistration = () => {
    for (const f of fields) {
      if (f.type === "checkbox") { if (!values[f.key]) { toast.error(`Please accept: ${f.checkbox_label || f.label}`); return; } }
      else if (!String(values[f.key] || "").trim()) { toast.error(`Please complete: ${f.label}`); return; }
    }
    // Validate every phone-like field: built-in mobile, tel-type fields, and any
    // custom field whose label mentions phone/mobile/whatsapp.
    for (const f of fields) {
      const lbl = (f.label || "").toLowerCase();
      const isPhone = f.type === "tel" || lbl.includes("phone") || lbl.includes("mobile") || lbl.includes("whatsapp");
      const val = String(values[f.key] || "").trim();
      if (isPhone && val && !isValidPhone(val)) { toast.error(`Please enter a valid 10-digit mobile number for: ${f.label}`); return; }
    }
    setStep(2);
  };

  const createReg = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/registrations", { page_id: page.id, values });
      setReg(data); setRemaining(page.final_fee); setPaidSoFar(0); setStep(3);
    } catch (e) { failed(e); }
    setBusy(false);
  };

  const onFile = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setOcrRunning(true); setDetected(null);
    try {
      const upload = await shrinkImage(file);
      const fd = new FormData(); fd.append("file", upload, upload.name || "screenshot.jpg"); fd.append("registration_id", reg.id);
      const { data } = await api.post("/public/upload-proof", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 150000 });
      const d = data.detected || {};
      const det = {
        utr: d.transaction_id || "", amount: d.amount ?? "", upi_id: d.payee_upi_id || "",
        payee_name: d.payee_name || "", date: d.date || "", time: d.time || "",
        extraction_id: data.extraction_id || null,
      };
      setDetected(det);
      const amountRead = det.amount !== "" && det.amount !== null && !isNaN(Number(det.amount));
      if (amountRead) await submitPayment(det);
      else setStep(5);
    } catch (e) {
      failed(e, e.response ? null : "Upload failed — please check your internet connection and try again.");
      setPreview("");
    }
    setOcrRunning(false);
  };

  const submitPayment = async (det = detected) => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/submit-payment", {
        registration_id: reg.id,
        extraction_id: det.extraction_id,
        utr: det.utr, amount: det.amount === "" ? null : Number(det.amount),
        upi_id: det.upi_id, payee_name: det.payee_name, date: det.date, time: det.time,
        payment_method: paymentMethod,
      });
      if (data.duplicate) { setBusy(false); setDuplicateOpen(true); return; }
      setPaidSoFar(data.total_paid || 0);
      setRemaining(data.remaining ?? 0);
      if (data.fully_paid) { setResult(data); setStep(6); }
      else if (data.this_status === "verified" && data.can_pay_again) {
        toast.success(`Partial payment of ${inr(data.this_payment_amount)} received. ${inr(data.remaining)} still remaining.`);
        setPreview(""); setDetected(null); setStep(3);
      } else {
        setResult(data); setStep(6);
      }
    } catch (e) { failed(e); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-0 sm:py-8 overflow-x-hidden">
      <div className="max-w-xl lg:max-w-2xl mx-auto sm:px-4">
        <div className="bg-white sm:rounded-2xl sm:border border-slate-200 sm:shadow-xl overflow-hidden min-h-screen sm:min-h-0">
          <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <Logo size="sm" />
            <div className="text-xs font-semibold text-slate-400">{page.school_name || "GDIOT PAY"}</div>
          </header>

          {langs.length > 1 && (
            <div className="px-4 py-2.5 bg-blue-50/60 border-b border-blue-100 flex flex-wrap items-center gap-2" data-testid="public-lang-picker">
              <span className="text-xs font-medium text-slate-600">Select your language / भाषा निवडा:</span>
              <div className="inline-flex bg-white rounded-full p-0.5 border border-slate-200">
                {langs.map((l) => (
                  <button key={l} onClick={() => setLang(l)} data-testid={`public-lang-${l}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${(langs.includes(lang) ? lang : "en") === l ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{LANG_LABELS[l]}</button>
                ))}
              </div>
            </div>
          )}

          {step > 0 && step < 6 && (
            <div className="flex items-center gap-1 px-4 pt-4">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1"><div className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-blue-600" : "bg-slate-200"}`} /></div>
              ))}
            </div>
          )}

          <div className="p-5 sm:p-6 animate-fade-up" key={step}>
            {step === 0 && <Landing page={page} onStart={() => setStep(1)} />}
            {step === 1 && <RegForm fields={fields} values={values} setV={setV} onBack={() => setStep(0)} onNext={submitRegistration} />}
            {step === 2 && <Summary page={page} fields={fields} values={values} fee={fee} busy={busy} onBack={() => setStep(1)} onNext={createReg} />}
            {step === 3 && (page.closed || page.paused) && <ClosedNotice paused={page.paused} msg={page.status_message} />}
            {step === 3 && !(page.closed || page.paused) && <Payment page={page} fee={remaining ?? fee} required={fee} paidSoFar={paidSoFar} reg={reg} onNext={(autoVerify) => {
              if (reg?.id) api.post("/public/payment-session", { registration_id: reg.id }).catch(() => {});
              setStep(autoVerify ? 7 : 4);
            }} onSelectApp={setPaymentMethod}
              onVerified={(st) => { setResult(st); setStep(6); }}
              onPartial={(st) => { setPaidSoFar(st.total_paid); setRemaining(st.remaining); toast.success(`Payment of ${inr(st.total_paid)} verified. ${inr(st.remaining)} still remaining.`); }} />}
            {step === 7 && <AutoVerifyWait reg={reg} onUpload={() => setStep(4)}
              onVerified={(st) => { setResult(st); setStep(6); }}
              onPartial={(st) => { setPaidSoFar(st.total_paid); setRemaining(st.remaining); toast.success(`Payment of ${inr(st.total_paid)} verified. ${inr(st.remaining)} still remaining.`); setStep(3); }} />}
            {step === 4 && <ProofUpload fileRef={fileRef} onFile={onFile} ocrRunning={ocrRunning} busy={busy} preview={preview} onBack={() => setStep(3)} />}
            {step === 5 && <ReviewDetected detected={detected} fee={fee} busy={busy} onSubmit={() => submitPayment()} onReupload={() => { setStep(4); setPreview(""); setDetected(null); }} />}
            {step === 6 && <Confirmation result={result} page={page} values={values} fee={fee} />}
            {step >= 1 && <SupportBlock page={page} uploadStep={step === 4} reg={reg} values={values} />}
            {step >= 1 && (page.refund_policy || "").trim() && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5" data-testid="refund-policy-block">
                <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1">Refund Policy</p>
                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">{page.refund_policy}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {duplicateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="duplicate-payment-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDuplicateOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-fade-up">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-9 h-9 text-amber-600" />
            </div>
            <h2 className="font-head text-xl font-extrabold text-slate-900">Payment Already Used</h2>
            <p className="text-slate-500 text-sm mt-2 leading-relaxed break-words">
              This payment transaction has already been submitted or used for another registration. This payment cannot be accepted again. Please make a new payment and submit the new transaction/payment reference.
            </p>
            <div className="mt-6 space-y-2.5">
              <button data-testid="duplicate-make-new-payment-btn" onClick={() => { setDuplicateOpen(false); setPreview(""); setDetected(null); setStep(3); }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl active:scale-[0.99] transition-all">Make a New Payment</button>
              <button data-testid="duplicate-close-btn" onClick={() => setDuplicateOpen(false)}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-3 rounded-xl">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Landing({ page, onStart }) {
  return (
    <div className="space-y-5">
      {page.cover_image && <img src={fileUrl(page.cover_image)} alt="cover" className="w-full h-auto object-contain rounded-xl bg-white" />}
      <div className="flex flex-wrap gap-2">
        {page.program_name && <Badge>{page.program_name}</Badge>}
        {page.level_name && <Badge>{page.level_name}</Badge>}
        {page.batch_name && <Badge>{page.batch_name}</Badge>}
      </div>
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">{page.title}</h1>
        {page.subtitle && <p className="text-slate-500 mt-1.5">{page.subtitle}</p>}
      </div>
      {page.description && <div className="rich-content text-slate-600 text-sm leading-relaxed break-words" data-testid="page-rich-description" dangerouslySetInnerHTML={{ __html: cleanHtml(page.description) }} />}
      <button onClick={onStart} data-testid="public-landing-register-cta"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl shadow-lg shadow-blue-600/25 active:scale-[0.99] transition-all flex items-center justify-center gap-2 text-base">
        Register Now <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

function SupportBlock({ page, uploadStep, reg, values }) {
  const phone = (page.support_phone || "").trim();
  const tel = phone.replace(/[^\d+]/g, "");
  if (uploadStep) {
    const waPhone = (page.support_whatsapp || "").replace(/\D/g, "").slice(-10);
    const wa = `91${waPhone}`;
    const student = (reg?.student_name || studentNameFrom(page.form_fields || DEFAULT_FIELDS, values) || "").trim();
    const text = `Hello, I am unable to upload the payment screenshot. Student Name: ${student}${reg?.registration_id ? `, Registration ID: ${reg.registration_id}` : ""}. Sending the screenshot here.`;
    return (
      <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-center" data-testid="upload-help-whatsapp-block">
        <p className="text-sm text-emerald-900 font-medium">Not able to upload the screenshot?</p>
        <p className="text-xs text-emerald-800 mt-1">Please send the payment screenshot on this WhatsApp number along with the <span className="font-semibold">student name</span>. Our team will verify and approve it for you.</p>
        <a href={`https://wa.me/${wa}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="upload-help-whatsapp-link"
          className="mt-2.5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2.5 rounded-xl active:scale-[0.98] transition-transform">
          <MessageCircle className="w-4 h-4" /> WhatsApp {waPhone}
        </a>
      </div>
    );
  }
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-center" data-testid="support-block">
      <p className="text-xs text-slate-500">For any technical help, contact us or call us.</p>
      {phone ? (
        <a href={`tel:${tel}`} data-testid="support-call-link" className="mt-1.5 inline-flex items-center gap-2 text-blue-600 font-semibold text-base hover:text-blue-700 active:scale-[0.99]">
          <Phone className="w-4 h-4" /> {phone}
        </a>
      ) : (
        <p className="mt-1.5 text-sm text-slate-600">Please reach out to our support team.</p>
      )}
    </div>
  );
}


function RegForm({ fields, values, setV, onBack, onNext }) {
  // Render fields in the EXACT order the admin arranged in the page builder.
  // A section heading is shown only when the group changes — no re-grouping,
  // so a field never jumps away from its configured position.
  return (
    <div className="space-y-5">
      <StepTitle title="Registration Details" sub="Please fill in the details below." />
      {fields.map((f, i) => {
        const g = f.group || "Additional Information";
        const prevG = i > 0 ? (fields[i - 1].group || "Additional Information") : null;
        return (
          <div key={f.key} className="space-y-3">
            {g !== prevG && <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pt-1">{g}</div>}
            <DynField f={f} value={values[f.key] || ""} onChange={(v) => setV(f.key, v)} />
          </div>
        );
      })}
      <div className="flex gap-3">
        <button onClick={onBack} className="px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium flex items-center gap-2"><ArrowLeft className="w-4 h-4" /></button>
        <button onClick={onNext} data-testid="registration-form-submit-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">Continue <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function DynField({ f, value, onChange }) {
  const label = f.label + (f.required ? " *" : "");
  const testid = `reg-${f.key.replace(/_/g, "-")}`;
  if (f.type === "checkbox") return (
    <div>
      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5 rounded border-slate-300 text-blue-600" />
        <span>{f.checkbox_label || f.label}{f.required ? " *" : ""}</span>
      </label>
      {f.help_text && <p className="text-xs text-slate-400 mt-1 ml-6">{f.help_text}</p>}
    </div>
  );
  if (f.type === "select") return (
    <div>
      <Select label={label} value={value} onChange={onChange} options={f.options || []} testid={testid} placeholder={`Select ${f.label}`} />
      {f.help_text && <p className="text-xs text-slate-400 mt-1">{f.help_text}</p>}
    </div>
  );
  if (f.type === "textarea") return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <textarea rows={2} className={fieldCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={f.placeholder || ""} />
      {f.help_text && <p className="text-xs text-slate-400 mt-1">{f.help_text}</p>}
    </div>
  );
  const t = ["date", "email", "number", "tel"].includes(f.type) ? f.type : "text";
  return (
    <div>
      <Input label={label} type={t} value={value} onChange={onChange} testid={testid} placeholder={f.placeholder} />
      {f.help_text && <p className="text-xs text-slate-400 mt-1">{f.help_text}</p>}
    </div>
  );
}

function Summary({ page, fields, values, fee, busy, onBack, onNext }) {
  return (
    <div className="space-y-5">
      <StepTitle title="Registration Summary" sub="Please review before proceeding to payment." />
      <div className="bg-slate-50 rounded-2xl p-5 space-y-3 text-sm">
        {summaryRows(page, fields, values).map((r) => <div key={r.id} data-testid={`summary-row-${r.id}`}><Row k={r.k} v={r.v} /></div>)}
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-center justify-between">
        <span className="font-medium text-slate-600">Amount Payable</span>
        <span className="font-head text-2xl font-extrabold text-blue-600">{inr(fee)}</span>
      </div>
      <div className="flex gap-3">
        <button onClick={onBack} className="px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"><ArrowLeft className="w-4 h-4" /></button>
        <button onClick={onNext} disabled={busy} data-testid="fee-summary-proceed-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Proceed to Payment <ArrowRight className="w-4 h-4" /></>}</button>
      </div>
    </div>
  );
}

function Payment({ page, fee: baseFee, required, paidSoFar, reg, onNext, onVerified, onPartial }) {
  const [qrOpen, setQrOpen] = useState(false);
  const [intent, setIntent] = useState(null);
  useEffect(() => {
    if (!reg?.id) return;
    api.post("/public/payment-intent", { registration_id: reg.id }).then(({ data }) => setIntent(data)).catch(() => setIntent({ enabled: false }));
  }, [reg?.id, baseFee]);
  const auto = !!intent?.enabled;
  const unique = auto && intent.unique;
  usePaymentWatch(reg?.id, auto, onVerified, onPartial);
  const fee = unique ? intent.amount : baseFee;
  const qrSrc = unique ? intent.qr : (page.qr_image ? fileUrl(page.qr_image) : "");
  useEffect(() => {
    if (!qrOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setQrOpen(false); };
    window.addEventListener("keydown", onKey);
    window.history.pushState({ gdiotQr: true }, "");
    const onPop = () => setQrOpen(false);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      if (window.history.state && window.history.state.gdiotQr) window.history.back();
    };
  }, [qrOpen]);

  if (intent === null) return <div className="py-16 flex justify-center" data-testid="payment-intent-loading"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  const copyUpi = () => { navigator.clipboard.writeText(page.upi_id || ""); toast.success("UPI ID copied!"); };

  return (
    <div className="space-y-5">
      <StepTitle title="Make Payment" sub={`Registration ID: ${reg.registration_id}`} />
      {paidSoFar > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm" data-testid="payment-progress">
          <div className="font-semibold text-emerald-800 mb-2">Partial Payment Received</div>
          <div className="space-y-1 text-slate-600">
            <div className="flex justify-between"><span>Required Amount</span><span className="font-medium">{inr(required)}</span></div>
            <div className="flex justify-between"><span>Paid so far</span><span className="font-medium text-emerald-700" data-testid="paid-so-far">{inr(paidSoFar)}</span></div>
            <div className="flex justify-between border-t border-emerald-200 pt-1.5 mt-1.5"><span className="font-semibold text-slate-700">Remaining</span><span className="font-bold text-blue-600" data-testid="remaining-amount">{inr(fee)}</span></div>
          </div>
        </div>
      )}
      <div className="text-center bg-blue-600 rounded-2xl p-5 sm:p-6 text-white">
        <div className="text-sm opacity-80">{paidSoFar > 0 ? "Remaining Amount" : "Amount Payable"}</div>
        <div className="font-head text-3xl sm:text-4xl font-extrabold mt-1 break-words" data-testid="payment-amount">{inr(fee)}</div>
        <div className="text-xs opacity-80 mt-1">UPI Payment</div>
      </div>

      {unique && (
        <div className="rounded-2xl border-2 border-blue-300 bg-blue-50 p-4 text-sm text-blue-900" data-testid="unique-amount-notice">
          <p className="font-bold">Please pay exactly <span className="text-blue-700" data-testid="unique-amount-value">{inr(fee)}</span></p>
          <p className="mt-1 text-blue-800">The extra paise help us identify and confirm your payment automatically. Scan the QR below — the exact amount is already filled in.</p>
        </div>
      )}
      {auto ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="auto-verify-notice">
          <p className="font-semibold">Your payment will be verified automatically.</p>
          <p className="mt-1 text-emerald-800">As soon as your payment goes through, this page confirms it automatically — no screenshot needed. You can also tap <b>"I have completed the payment"</b> below.</p>
          <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="auto-watch-indicator"><Loader2 className="w-3 h-3 animate-spin" /> Watching for your payment…</p>
        </div>
      ) : (
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5" data-testid="upload-instruction-alert">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-amber-400 text-white flex items-center justify-center">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="font-head font-bold text-amber-900 text-base leading-snug">One last step — don't skip this!</p>
            <p className="text-sm text-amber-800 mt-1">
              Paying the amount is <span className="font-semibold">not enough</span>. To confirm your registration you
              <span className="font-semibold"> must upload the payment screenshot here</span> on this page.
            </p>
            <ol className="mt-3 space-y-1.5 text-sm text-amber-900">
              <li className="flex gap-2"><span className="font-bold">1.</span><span>Pay using the QR code / UPI ID below.</span></li>
              <li className="flex gap-2"><span className="font-bold">2.</span><span>Take a <span className="font-semibold">screenshot of the successful payment</span> (showing the amount &amp; date/time).</span></li>
              <li className="flex gap-2"><span className="font-bold">3.</span><span>Come back here and tap <span className="font-semibold">"Upload the Payment Screenshot"</span> below to finish.</span></li>
            </ol>
            <p className="text-xs text-amber-700 mt-3">Your seat is confirmed only after the screenshot is uploaded and verified.</p>
          </div>
        </div>
      </div>
      )}

      <div className="flex items-center gap-3 text-slate-400 text-xs font-semibold">
        <div className="flex-1 h-px bg-slate-200" /> Pay via UPI <div className="flex-1 h-px bg-slate-200" />
      </div>

      <div id="gdiot-qr" className="flex flex-col items-center gap-2">
        <div className="text-sm font-semibold text-slate-700">Scan QR Code</div>
        {qrSrc ? (
          <>
            <button type="button" onClick={() => setQrOpen(true)} data-testid="payment-qr-enlarge-btn"
              className="relative rounded-xl border border-slate-200 bg-white p-2.5 active:scale-[0.99] transition-transform hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <img src={qrSrc} alt="Payment QR" className="w-60 h-60 sm:w-64 sm:h-64 lg:w-72 lg:h-72 max-w-full object-contain" data-testid="payment-qr-image" />
              <span className="absolute bottom-2 right-2 bg-slate-900/70 text-white rounded-lg p-1.5"><Maximize2 className="w-4 h-4" /></span>
            </button>
            <p className="text-xs font-semibold text-blue-600 flex items-center gap-1" data-testid="qr-enlarge-hint"><Maximize2 className="w-3 h-3" /> Tap QR to enlarge</p>
            <p className="text-xs text-slate-400 text-center">Scan with any UPI app, or tap the QR to view it full screen.</p>
            {unique && intent.upi_link && (
              <a href={intent.upi_link} className="sm:hidden mt-1 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 rounded-xl" data-testid="open-upi-app-btn">Pay {inr(fee)} with a UPI app on this phone</a>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-400 py-6" data-testid="qr-fallback">
            <QrCode className="w-10 h-10" />
            {page.payment_config_error
              ? <p className="text-sm text-center text-amber-600 font-medium max-w-xs">A QR code isn't set up for this amount yet. Please pay using the UPI ID below.</p>
              : <p className="text-sm">Use the UPI ID below to pay</p>}
          </div>
        )}
      </div>

      {page.upi_id && (
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <div><div className="text-xs text-slate-400 uppercase tracking-wider">UPI ID</div><div className="font-mono font-semibold text-slate-800">{page.upi_id}</div></div>
          <button onClick={copyUpi} data-testid="payment-qr-copy-upi-btn" className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5"><Copy className="w-4 h-4" /> Copy UPI ID</button>
        </div>
      )}

      <div className="border-t border-slate-100 pt-4">
        <p className="text-center text-sm font-medium text-slate-600 mb-3">Already completed the payment?</p>
        {auto ? (
          <button onClick={() => onNext(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2" data-testid="ive-paid-auto-btn">I have completed the payment <ArrowRight className="w-4 h-4" /></button>
        ) : (
        <button onClick={() => onNext(false)} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2" data-testid="ive-paid-btn">Payment Completed? Upload the Payment Screenshot <ArrowRight className="w-4 h-4" /></button>
        )}
      </div>

      {qrOpen && qrSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="qr-enlarge-modal" onClick={() => setQrOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 sm:p-6 my-auto animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="qr-modal-content">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-head text-lg font-bold text-slate-900">Payment QR Code</h2>
              <button onClick={() => setQrOpen(false)} data-testid="qr-modal-close-btn" aria-label="Close"
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center active:scale-95 transition-all">
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <img src={qrSrc} alt="Payment QR enlarged" className="w-64 h-64 sm:w-72 sm:h-72 max-w-full object-contain" data-testid="qr-modal-image" />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-6 py-2.5 text-center">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Amount</div>
                <div className="font-head text-2xl sm:text-3xl font-extrabold text-blue-600" data-testid="qr-modal-amount">{inr(fee)}</div>
              </div>
              <p className="text-center text-sm text-slate-600 leading-relaxed">
                Scan this QR code using your UPI app to make the payment.
                <span className="block text-slate-400 mt-1">Make sure the payment amount matches the amount shown on this page.</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Polls payment status every 3s for 10 minutes; fires onVerified / onPartial (new money only).
function usePaymentWatch(regId, active, onVerified, onPartial) {
  const [st, setSt] = useState(null);
  const [checking, setChecking] = useState(false);
  const [tries, setTries] = useState(0);
  const cbs = useRef({ onVerified, onPartial });
  cbs.current = { onVerified, onPartial };
  const startPaid = useRef(null);
  const check = useCallback(async (manual) => {
    if (manual) setChecking(true);
    try {
      const { data } = await api.get(`/public/payment-status/${regId}`);
      setSt(data);
      if (startPaid.current === null) startPaid.current = data.total_paid;
      if (data.state === "verified") cbs.current.onVerified(data);
      else if (data.state === "partial" && data.total_paid > startPaid.current) cbs.current.onPartial(data);
    } catch { /* keep waiting */ }
    if (manual) setChecking(false);
    setTries((t) => t + 1);
  }, [regId]);
  useEffect(() => {
    if (!active || !regId) return undefined;
    check();
    const t = setInterval(() => check(), 3000);
    const stop = setTimeout(() => clearInterval(t), 10 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [check, active, regId]);
  return { st, checking, tries, check };
}

function AutoVerifyWait({ reg, onUpload, onVerified, onPartial }) {
  const { st, checking, tries, check } = usePaymentWatch(reg.id, true, onVerified, onPartial);
  const late = tries > 40;
  return (
    <div className="space-y-5 text-center" data-testid="auto-verify-wait">
      <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
        {late ? <AlertCircle className="w-9 h-9 text-amber-500" /> : <Loader2 className="w-9 h-9 text-blue-600 animate-spin" />}
      </div>
      <div>
        <h2 className="font-head text-xl font-bold text-slate-900" data-testid="auto-verify-title">{late ? "Payment Verification Pending" : "Payment Verification in Progress"}</h2>
        {late ? (
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">We haven't been able to verify your payment yet. If you have already paid, please wait while we check the transaction. If it is still not verified, you can upload your payment screenshot for manual review.</p>
        ) : (
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">Thank you for making your payment! We are checking your payment confirmation automatically. This usually takes a short time, but it may occasionally take longer.</p>
        )}
      </div>
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-medium text-amber-900" data-testid="no-repay-warning">
        Please do not make another payment while verification is in progress.
      </div>
      <div className="text-xs text-slate-400">Registration ID: <span className="font-mono">{reg.registration_id}</span>{st?.state === "review" ? " · Screenshot under review" : ""}</div>
      <button onClick={() => check(true)} disabled={checking} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60" data-testid="check-payment-status-btn">
        {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check Payment Status
      </button>
      <div>
        <p className="text-xs text-slate-500 mb-2">If your payment is not verified, you can upload your payment screenshot for further assistance.</p>
        <button onClick={onUpload} className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="wait-upload-screenshot-btn">
          <Upload className="w-4 h-4" /> Upload Payment Screenshot
        </button>
      </div>
    </div>
  );
}

// Downscale large phone photos to a JPEG before upload; falls back to the original file.
async function shrinkImage(file) {
  if (!file.type.startsWith("image/") || file.size < 700 * 1024) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.85));
    return blob ? new File([blob], "screenshot.jpg", { type: "image/jpeg" }) : file;
  } catch { return file; }
}

function ProofUpload({ fileRef, onFile, ocrRunning, busy, preview, onBack }) {
  const working = ocrRunning || busy;
  return (
    <div className="space-y-5 relative">
      <StepTitle title="Upload the Payment Screenshot" sub="If the payment is completed, please upload the payment screenshot. It should clearly show the amount paid and the UPI ID — we'll read the details automatically." />
      <label htmlFor="payment-proof-input" data-testid="payment-proof-upload-zone"
        className={`w-full border-2 border-dashed border-blue-300 bg-blue-50/50 rounded-2xl py-12 flex flex-col items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors cursor-pointer select-none ${working ? "pointer-events-none" : ""}`}>
        {working ? (<><ScanLine className="w-10 h-10 mb-2 animate-pulse" /><span className="font-medium">Verifying your payment…</span><span className="text-xs text-blue-400 mt-1">Please wait, this takes a few seconds</span></>)
          : preview ? (<><img src={preview} alt="preview" className="max-h-48 rounded-lg" /><span className="text-xs text-blue-500 mt-2 font-medium">Tap to choose a different screenshot</span></>)
          : (<><Upload className="w-10 h-10 mb-2" /><span className="font-semibold text-base">Tap to upload the payment screenshot here</span><span className="text-xs text-blue-400 mt-1">JPG, PNG or PDF</span></>)}
      </label>
      {working && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-center" data-testid="ocr-verifying-note">
          <p className="text-sm font-medium text-blue-700">Verifying your payment details…</p>
          <p className="text-xs text-blue-500 mt-0.5">This usually takes a few seconds. Please wait — don't close or refresh this page.</p>
        </div>
      )}
      <input id="payment-proof-input" ref={fileRef} type="file" accept="image/*,.heic,.heif,application/pdf" disabled={working} data-testid="payment-proof-input"
        className="absolute w-px h-px opacity-0 overflow-hidden" style={{ clip: "rect(0 0 0 0)" }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onFile(f); }} />
      {!ocrRunning && <button onClick={onBack} className="w-full text-slate-500 text-sm flex items-center justify-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to payment</button>}
    </div>
  );
}

function ReviewDetected({ detected, fee, busy, onSubmit, onReupload }) {
  const amt = detected.amount;
  const hasAmount = amt !== "" && amt !== null && amt !== undefined && !isNaN(Number(amt));
  const ready = hasAmount;
  const Locked = ({ label, value, mono, testid }) => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <div data-testid={testid} className={`flex items-center justify-between gap-2 bg-slate-100 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>
        <span className="truncate">{value || <span className="text-slate-400 italic font-sans">Not detected</span>}</span>
        <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      <StepTitle title="Payment Details Detected" sub="These were read from your screenshot and cannot be edited." />
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-500 flex items-center gap-2" data-testid="ocr-locked-note"><Lock className="w-4 h-4 text-slate-400 flex-shrink-0" /> Payment details are automatically extracted and cannot be edited.</div>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Locked label="Paid Amount (₹)" value={hasAmount ? inr(Number(detected.amount)) : ""} testid="ocr-amount" />
          <Locked label="Payee UPI ID" value={detected.upi_id} mono testid="ocr-upi" />
        </div>
        {detected.utr && String(detected.utr).trim() && <Locked label="Transaction Reference" value={detected.utr} mono testid="ocr-utr" />}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Locked label="Date" value={detected.date} testid="ocr-date" />
          <Locked label="Payee Name" value={detected.payee_name} testid="ocr-payee" />
        </div>
      </div>
      {!hasAmount && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700 flex items-start gap-2" data-testid="ocr-incomplete-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>The paid amount could not be read. Please upload a clearer payment screenshot.</span>
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={onReupload} data-testid="ocr-reupload-btn" className="px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium flex items-center gap-2"><Upload className="w-4 h-4" /> Re-upload</button>
        <button onClick={onSubmit} disabled={busy || !ready} data-testid="ocr-review-confirm-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying payment…</> : <>Check &amp; Submit <ArrowRight className="w-4 h-4" /></>}</button>
      </div>
    </div>
  );
}

function Confirmation({ result, page, values, fee }) {
  const verified = result.status === "verified";
  const copyId = () => { navigator.clipboard.writeText(result.registration_id); toast.success("Registration ID copied"); };
  const [invoice, setInvoice] = useState(null);
  useEffect(() => {
    if (!verified) return;
    api.get(`/public/invoice/${result.registration_id}`).then((r) => setInvoice(r.data)).catch(() => setInvoice(false));
  }, [verified, result.registration_id]);

  if (!verified) {
    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center pt-2"><Logo size="md" showTagline={false} align="center" /></div>
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center bg-amber-100"><Clock className="w-11 h-11 text-amber-600" /></div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900 mt-4">Screenshot Received</h1>
          <p className="text-slate-500 text-sm mt-1.5 max-w-xs">{result.message}</p>
        </div>
        <div className="bg-slate-50 rounded-2xl p-5 space-y-3 text-sm text-left">
          <div className="flex items-center justify-between">
            <div><div className="text-xs text-slate-400 uppercase tracking-wider">Registration ID</div><div className="font-mono font-bold text-blue-600 text-base">{result.registration_id}</div></div>
            <button onClick={copyId} className="text-slate-400 hover:text-blue-600" data-testid="copy-reg-id"><CopyIcon className="w-4 h-4" /></button>
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <Row k="Student" v={result.student_name || studentNameFrom(page.form_fields || DEFAULT_FIELDS, values)} />
            <Row k="Required" v={inr(result.required ?? fee)} />
            <Row k="Total Paid" v={inr(result.total_paid ?? 0)} />
            {result.remaining > 0 && <Row k="Remaining" v={inr(result.remaining)} />}
            <Row k="Payment Status" v={<span className="capitalize font-semibold">{(result.status || "").replace(/_/g, " ")}</span>} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center flex flex-col items-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center bg-emerald-100"><CheckCircle2 className="w-11 h-11 text-emerald-600" /></div>
        <h1 className="font-head text-2xl font-extrabold text-slate-900 mt-4" data-testid="congrats-heading">Congratulations!</h1>
        <p className="text-slate-500 text-sm mt-1.5 max-w-xs">Your registration process has been successfully completed.</p>
      </div>

      <div className="bg-slate-50 rounded-2xl p-5 space-y-3 text-sm text-left">
        <div className="flex items-center justify-between">
          <div><div className="text-xs text-slate-400 uppercase tracking-wider">Registration ID</div><div className="font-mono font-bold text-blue-600 text-base">{result.registration_id}</div></div>
          <button onClick={copyId} className="text-slate-400 hover:text-blue-600" data-testid="copy-reg-id"><CopyIcon className="w-4 h-4" /></button>
        </div>
        <div className="border-t border-slate-200 pt-3 space-y-2">
          {[["Student", studentNameFrom(page.form_fields || DEFAULT_FIELDS, values) || invoice?.student_name],
            ["Program", page.program_name || invoice?.program], ["Level", page.level_name || invoice?.level],
            ["Batch", page.batch_name || invoice?.batch]].filter(([, v]) => v).map(([k, v]) => <Row key={k} k={k} v={v} />)}
        </div>
      </div>

      {result.whatsapp_link && (
        <a href={result.whatsapp_link} target="_blank" rel="noreferrer" data-testid="join-whatsapp-group-btn"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-600/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-base">
          <MessageCircle className="w-5 h-5" /> Join WhatsApp Group
        </a>
      )}

      <div className="space-y-3">
        <div>
          <h2 className="font-head text-lg font-bold text-slate-900">Your Invoice</h2>
          <p className="text-slate-500 text-sm">Your payment and registration details are shown below.</p>
        </div>
        {invoice === null ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Preparing invoice…</div>
        ) : invoice === false ? (
          <div className="text-sm text-slate-400 py-4 text-center">Invoice could not be loaded.</div>
        ) : (
          <>
            <InvoicePreview d={invoice} />
            <a href={`${BACKEND_URL}/api/public/invoice/${result.registration_id}/pdf`} target="_blank" rel="noreferrer"
              data-testid="download-invoice-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Download Invoice
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function InvoicePreview({ d }) {
  const c = d.company || {};
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto" data-testid="invoice-preview">
      <div className="min-w-[560px] p-4 sm:p-5 text-[11px] text-slate-700">
        <div className="flex justify-between gap-4">
          <div className="w-3/5">
            <div className="font-bold text-sm text-slate-900">{c.name}</div>
            <div className="text-slate-500 leading-snug">{c.address}</div>
            <div className="text-slate-500">{c.place}</div>
            {c.gstin && <div className="text-slate-500">GSTIN {c.gstin}</div>}
            <div className="text-slate-500">{c.email}</div>
            <div className="text-slate-500">{c.website}</div>
          </div>
          <div className="w-2/5 text-right">
            <div className="font-extrabold text-base tracking-wide text-slate-900">{d.title}</div>
            <table className="ml-auto mt-2 text-[11px]">
              <tbody>
                <tr><td className="text-slate-400 pr-3 text-left">Invoice#</td><td className="font-semibold text-right">{d.invoice_no}</td></tr>
                <tr><td className="text-slate-400 pr-3 text-left">Invoice Date</td><td className="text-right">{d.invoice_date}</td></tr>
                <tr><td className="text-slate-400 pr-3 text-left">Terms</td><td className="text-right">{d.terms}</td></tr>
                <tr><td className="text-slate-400 pr-3 text-left">Due Date</td><td className="text-right">{d.due_date}</td></tr>
                <tr><td className="text-slate-400 pr-3 text-left">Order#</td><td className="text-right font-mono">{d.order_no}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="border border-slate-200 rounded p-2"><div className="font-semibold">Bill To</div><div className="text-slate-500 whitespace-pre-line">{(d.bill_to?.lines || []).join("\n")}</div></div>
          <div className="border border-slate-200 rounded p-2"><div className="font-semibold">Ship To</div><div className="text-slate-500 whitespace-pre-line">{(d.ship_to?.lines || []).join("\n") || "—"}</div></div>
        </div>
        <table className="w-full mt-4 border-collapse">
          <thead>
            <tr className="bg-slate-100 text-[10px] uppercase text-slate-500">
              {["#", "Item & Description", "HSN/SAC", "Qty", "Rate", "CGST", "SGST", "Amount"].map((h) => <th key={h} className="border border-slate-200 px-2 py-1.5 text-left">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(d.items || []).map((it, i) => (
              <tr key={i}>
                <td className="border border-slate-200 px-2 py-1.5 text-center">{i + 1}</td>
                <td className="border border-slate-200 px-2 py-1.5">{it.desc}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-center">{it.hsn || "-"}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-center">{it.qty}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-right">{inr(it.rate)}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-right">{inr(it.cgst_amt)}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-right">{inr(it.sgst_amt)}</td>
                <td className="border border-slate-200 px-2 py-1.5 text-right">{inr(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between gap-4 mt-4">
          <div className="w-1/2">
            <div className="font-semibold">Total In Words</div>
            <div className="text-slate-500 italic">{d.total_words}</div>
            <div className="font-semibold mt-3">Notes</div>
            <div className="text-slate-500">{d.notes}</div>
            {(d.txn_ids || []).map((t) => <div key={t} className="text-slate-500">Transaction ID: {t}</div>)}
          </div>
          <div className="w-1/2">
            <table className="w-full">
              <tbody>
                <tr><td className="text-right text-slate-500 pr-3 py-0.5">Sub Total</td><td className="text-right font-medium">{inr(d.sub_total)}</td></tr>
                <tr><td className="text-right text-slate-500 pr-3 py-0.5">CGST ({d.cgst_rate}%)</td><td className="text-right">{inr(d.cgst_amt)}</td></tr>
                <tr><td className="text-right text-slate-500 pr-3 py-0.5">SGST ({d.sgst_rate}%)</td><td className="text-right">{inr(d.sgst_amt)}</td></tr>
                <tr className="bg-slate-100"><td className="text-right font-semibold pr-3 py-1">Total</td><td className="text-right font-bold">{inr(d.total)}</td></tr>
                <tr><td className="text-right text-slate-500 pr-3 py-0.5">Balance Due</td><td className="text-right font-semibold">{inr(d.balance_due)}</td></tr>
              </tbody>
            </table>
            <div className="mt-6 text-right">
              {d.signature_url && (
                <div className="w-36 ml-auto mb-1 text-center">
                  <img src={d.signature_url} alt="Authorized signature" className="block mx-auto h-14 w-36 object-contain" data-testid="invoice-signature" />
                </div>
              )}
              <div className="border-t border-slate-400 pt-1 text-slate-500 text-[10px] w-36 text-center ml-auto">Authorized Signature</div>
            </div>
          </div>
        </div>
        <div className="text-center text-slate-400 text-[10px] border-t border-slate-200 pt-2 mt-4">{d.footer}</div>
      </div>
    </div>
  );
}

function Badge({ children }) { return <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{children}</span>; }
function StepTitle({ title, sub }) { return <div><h2 className="font-head text-xl font-bold text-slate-900">{title}</h2>{sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}</div>; }
function Section({ title, children }) { return <div className="space-y-3"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{title}</div>{children}</div>; }
function Row({ k, v }) { return <div className="flex items-center justify-between gap-3"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v || "—"}</span></div>; }
const fieldCls = "w-full bg-slate-50 border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
function Input({ label, value, onChange, type = "text", testid, mono, placeholder }) {
  const inputMode = type === "number" ? "decimal" : type === "tel" ? "numeric" : type === "email" ? "email" : undefined;
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <input type={type} inputMode={inputMode} placeholder={placeholder || ""} className={`${fieldCls} ${mono ? "font-mono" : ""}`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function Select({ label, value, onChange, options, testid, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <select className={fieldCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}><option value="">{placeholder || "Select…"}</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
    </div>
  );
}
function ClosedNotice({ paused, msg }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 flex gap-3" data-testid="page-closed-notice">
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-amber-900 text-sm" data-testid="page-closed-title">This Batch is Currently Full</p>
        <p className="text-sm text-amber-800 mt-1 leading-relaxed" data-testid="page-closed-message">{msg}</p>
      </div>
    </div>
  );
}

function CenterMsg({ title, msg }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 max-w-sm text-center">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="font-head text-xl font-bold text-slate-900">{title}</h1>
        <p className="text-slate-500 text-sm mt-2">{msg}</p>
      </div>
    </div>
  );
}
